import styled from '@emotion/styled';
import { FC, FocusEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from 'app/components/v2/constants/styles';
import { EvtKeys } from 'app/types';

import ListItem from './ListItem';
import KeyCodeListener from '../../EventListeners/KeyCodeListener';
import MenuItem from '../../Menu/MenuItem';
import Paragraph from '../../Text/Paragraph';
import { TypeAheadOption } from '..';

// Local Typings
interface ListProps {
  top?: number;
}
interface Props extends ListProps {
  onBlur: (evt: FocusEvent<HTMLUListElement>) => void;
  onFocus: (evt: FocusEvent<HTMLUListElement>) => void;
  onSelect: (option: TypeAheadOption) => void;
  options: TypeAheadOption[];
}

// Local Variables
const Wrapper = styled.ul<ListProps>(({ theme, top = 48 }) => ({
  backgroundColor: theme.semanticColors.card_background,
  border: '2px solid',
  borderColor: theme.semanticColors.card_border,
  maxHeight: 200,
  overflowY: 'auto',
  position: 'absolute',
  top,
  width: '100%',
  zIndex: styles.zIndex.overlay - 1,
}));

// Component Definition
const List: FC<Props> = ({
  options,
  ...props
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const hasResults = options.length > 0;

  const { t } = useTranslation('components');

  function handleDown() {
    const newIndex = selectedIndex + 1;
    if (options[newIndex]) {
      setSelectedIndex(newIndex);
    } else {
      setSelectedIndex(0);
    }
  }

  function handleUp() {
    const newIndex = selectedIndex - 1;
    if (options[newIndex]) {
      setSelectedIndex(newIndex);
    } else {
      setSelectedIndex(options.length - 1);
    }
  }

  function handleEnter(evt?: KeyboardEvent) {
    if (evt) {
      evt.preventDefault();
    }
    const option = options[selectedIndex];
    if (option) {
      props.onSelect(option);
    }
  }

  return (
    <Wrapper
      className="type-ahead_dropdown_list"
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      top={props.top}
    >
      {hasResults
        ? (
          <KeyCodeListener
            keyCodeActions={[
              {
                action: handleDown,
                key: EvtKeys.Down,
              },
              {
                action: handleEnter,
                key: EvtKeys.Enter,
              },
              {
                action: handleUp,
                key: EvtKeys.Up,
              },
            ]}
          >
            {options.map((option, index) => (
              <ListItem
              // eslint-disable-next-line react/no-array-index-key
                key={`${index}-${option.id || option.label}`}
                onSelect={props.onSelect}
                option={option}
                selected={index === selectedIndex}
              />
            ))}
          </KeyCodeListener>
        )
        : (
          <MenuItem
            isDisabled
            isSelected={false}
          >
            <Paragraph
              as="span"
              italic
            >
              {t('typeAhead.noResults', '(No results)')}
            </Paragraph>
          </MenuItem>
        )}
    </Wrapper>
  );
};

export default List;
